/**
 * MultiEnumR — multi-select enum renderer using CheckboxGroup.
 * Supports inline options and options_ref.
 */

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CheckboxGroup } from '../../../components/intake/fields';
import { MultiComboboxField } from '../../../components/intake/MultiComboboxField';
import type { FieldDef, RendererProps, RefDataOption } from '../types';
import { loadRefData, getRefDataSync } from '../reference-data/loader';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { buildValidationRules } from '../validation/fromSchema';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

export function MultiEnumR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);

  const [asyncOptions, setAsyncOptions] = useState<RefDataOption[] | null>(
    field.options_ref ? (getRefDataSync(field.options_ref) ?? null) : null,
  );

  useEffect(() => {
    if (field.options_ref && asyncOptions === null) {
      loadRefData(field.options_ref).then(setAsyncOptions);
    }
  }, [field.options_ref, asyncOptions]);

  const options: Array<{ value: string; label: string }> = field.options
    ? field.options
    : (asyncOptions ?? []);

  if (pdfMode) {
    const value: string[] = getValues(name) ?? [];
    const labels = value
      .map((v) => options.find((o) => o.value === v)?.label ?? v)
      .join(', ');
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {labels || <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  if (field.options_ref && asyncOptions === null) {
    return (
      <div>
        <span className="form-label">{label}</span>
        <div className="form-input animate-pulse bg-slate-100 text-transparent">Loading...</div>
      </div>
    );
  }

  // Long lists (countries, or any >8-option set) become a searchable chip
  // multi-select showing labels, not a wall of value-labelled checkboxes.
  const useCombobox = options.length > 8 || (field.options_ref?.includes('countries') ?? false);
  // RC-4: default-label required message matching the submit-gate summary.
  const rules = buildValidationRules(field);

  if (useCombobox) {
    return (
      <MultiComboboxField
        name={name}
        label={label}
        hint={resolveHelpText(field, audience)}
        required={field.required}
        options={options}
        tooltip={lookupFieldTooltip(field.canonical_name)}
        rules={rules}
      />
    );
  }

  return (
    <CheckboxGroup
      name={name}
      label={label}
      hint={resolveHelpText(field, audience)}
      required={field.required}
      options={options}
      tooltip={lookupFieldTooltip(field.canonical_name)}
      rules={rules}
    />
  );
}
